<template>
    <AppLayout>
        <div class="container px-4 py-6 mx-auto">
            <!-- Page Title + Status & Admin Buttons -->
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-800 dark:text-white md:text-3xl">Profile</h1>
                    <nav class="text-sm text-gray-500 dark:text-gray-300 mt-2">
                        <GoBack />
                    </nav>
                </div>
                <div class="flex flex-wrap gap-2">
                    <t-badge
                        v-if="currentKycVerified"
                        class="text-green-700 bg-green-100 px-4 py-2 rounded-md"
                        title="This KYC has been verified by admin"
                    >
                        Verified
                    </t-badge>
                    <t-badge
                        v-else-if="currentKycRejected"
                        class="text-red-700 bg-red-100 px-4 py-2 rounded-md"
                        title="This KYC has been rejected"
                    >
                        Rejected
                    </t-badge>
                    <t-badge
                        v-else
                        class="text-yellow-700 bg-yellow-100 px-4 py-2 rounded-md"
                        title="This KYC is awaiting review"
                    >
                        Pending
                    </t-badge>

                    <t-button
                        v-if="isAdmin && !currentKycVerified && !currentKycRejected"
                        variant="success"
                        @click="acceptRequest"
                        >Accept</t-button
                    >

                    <t-button
                        v-if="isAdmin && !currentKycVerified && !currentKycRejected"
                        variant="error"
                        class="px-4 py-2"
                        @click="showRejectModal"
                        >Reject</t-button
                    >

                    <t-button
                        v-if="currentKycRejected && $page.props.user.id === kyc.user_id"
                        variant="success"
                        @click="reSubmit"
                        >Resubmit</t-button
                    >
                </div>
            </div>

            <!-- Personal Info Card -->
            <div class="bg-white rounded-lg shadow p-6 mb-8">
                <h2 class="text-xl font-semibold text-gray-800 mb-4">Personal Information</h2>
                <div class="flex flex-col gap-6 md:flex-row">
                    <div class="flex justify-center md:w-1/3">
                        <img
                            :src="kyc.logo"
                            :alt="kyc.person_name"
                            class="w-40 h-40 rounded-full object-cover"
                        />
                    </div>
                    <div class="grid w-full gap-4 md:grid-cols-2">
                        <InfoField
                            label="Person Name"
                            :value="kyc.person_name"
                        />
                        <InfoField
                            label="Email"
                            :value="kyc.email"
                        />
                        <InfoField
                            label="Phone"
                            :value="kyc.phone"
                        />
                        <InfoField
                            label="DOB"
                            :value="kyc.dob | LL"
                        />
                        <InfoField
                            label="Gender"
                            :value="kyc.gender"
                        />
                    </div>
                </div>
            </div>

            <!-- Company Info Card -->
            <Disclosure>
                <template #title>
                    <h2 class="text-xl font-semibold text-gray-800">Company Information</h2>
                </template>
                <div class="grid gap-4 md:grid-cols-2 mt-4">
                    <InfoField
                        label="Company Name"
                        :value="kyc.company_name"
                    />
                    <InfoField
                        label="Registration Number"
                        :value="kyc.company_registration_number"
                    />
                    <InfoField
                        label="Business Address"
                        :value="kyc.business_address"
                    />
                    <InfoField
                        label="Bankers Name"
                        :value="kyc.bankers_name"
                    />
                    <InfoField
                        label="Bankers Address"
                        :value="kyc.bankers_address"
                    />
                    <InfoField
                        label="References Name"
                        :value="kyc.business_references_name"
                    />
                    <InfoField
                        label="References Address"
                        :value="kyc.business_references_address"
                    />
                </div>
            </Disclosure>

            <!-- Documents Section -->
            <Disclosure class="mt-8">
                <template #title>
                    <h2 class="text-xl font-semibold text-gray-800">Documents</h2>
                </template>
                <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 mt-4">
                    <DocumentCard
                        title="Identification Document"
                        :src="kyc.id_document"
                        @view="openImageInNewTab"
                        @download="downloadImage"
                    />
                    <DocumentCard
                        title="Registration Certificate"
                        :src="kyc.registration_certificate"
                        @view="openImageInNewTab"
                        @download="downloadImage"
                    />
                    <DocumentCard
                        v-if="kyc.vat_registration_certificate"
                        title="VAT Certificate"
                        :src="kyc.vat_registration_certificate"
                        @view="openImageInNewTab"
                        @download="downloadImage"
                    />
                </div>
                <div
                    v-if="kyc.images.length"
                    class="mt-10"
                >
                    <h3 class="text-lg font-medium text-gray-700 mb-4">Other Documents</h3>
                    <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
                        <DocumentCard
                            v-for="img in kyc.images"
                            :key="img.id"
                            :src="img.image_path"
                            @view="openImageInNewTab"
                            @download="downloadImage"
                        />
                    </div>
                </div>
                <div
                    v-else
                    class="text-sm text-gray-500 mt-6"
                >
                    No other document is submitted.
                </div>
                <div
                    v-if="kyc.notes && currentKycRejected"
                    class="mt-8 text-red-600"
                >
                    <strong>Notes:</strong> {{ kyc.notes }}
                </div>
            </Disclosure>

            <!-- Reject Modal -->
            <t-modal
                v-model="kycRejectModal"
                header="Reason For Rejection"
            >
                <t-textarea v-model="rejectionNotes" />
                <template #footer>
                    <div class="flex justify-between">
                        <t-button @click="hideRejectModal">Cancel</t-button>
                        <t-button @click="rejectRequest">Confirm Rejection</t-button>
                    </div>
                </template>
            </t-modal>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '@/Layouts/AppLayout.vue';
    import GoBack from '@/Components/GoBack.vue';

    import InfoField from '@/Components/InfoField.vue';
    import DocumentCard from '@/Components/DocumentCard.vue';
    import Disclosure from '@/Components/Disclosure.vue';

    export default {
        name: 'KycShow',

        components: {
            AppLayout,
            GoBack,
            InfoField,
            DocumentCard,
            Disclosure,
        },

        props: {
            kyc: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                kycRejectModal: false,
                rejectionNotes: null,
            };
        },

        computed: {
            currentKycVerified() {
                if (this.kyc.accepted_at) {
                    return true;
                }
                return false;
            },

            currentKycRejected() {
                if (this.kyc.deleted_at) {
                    return true;
                }
                return false;
            },
        },

        methods: {
            acceptRequest() {
                const payload = { _method: 'PUT', accepted: true };
                this.$inertia.post(this.kyc.path, payload, {
                    onFinish: () => {
                        this.goto('kyc.index');
                    },
                });
            },

            showRejectModal() {
                this.rejectionNotes = null;
                this.kycRejectModal = true;
            },

            hideRejectModal() {
                this.rejectionNotes = null;
                this.kycRejectModal = false;
            },

            rejectRequest() {
                const payload = { notes: this.rejectionNotes };
                this.$inertia.post(route('kyc.destroy', this.kyc.id), payload, {
                    onFinish: () => {
                        this.hideRejectModal();
                        this.goto('kyc.index');
                    },
                });
            },

            reSubmit() {
                this.$inertia.get(route('kyc.create'));
            },
        },
    };
</script>
